import { widgetRegionAtom } from '@/atoms/integrationAuthAtoms';
import { useLocalizedCountries } from '@/hooks/useLocalizedCountries';
import { cn } from '@/lib/cn';
import { countriesToRegion, filterCountriesByRegion } from '@/lists/countriesToRegion';
import { Button } from '@heidi/design-system/button';
import { ComboboxTrigger } from '@heidi/design-system/combobox';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
} from '@heidi/design-system/command';
import { Popover, PopoverContent, PopoverTrigger } from '@heidi/design-system/popover';
import { ScrollArea } from '@heidi/design-system/scroll-area';
import { Tooltip, TooltipContent, TooltipTrigger } from '@heidi/design-system/tooltip';
import { TypographyP2 } from '@heidi/design-system/typography';
import { Region } from '@heidi/schema/ml-scribe';
import { sortedCountries, type CountryType } from '@heidi/utils/countries';
import { useAtomValue } from 'jotai';
import { X } from 'lucide-react';
import Image from 'next/image';
import {
  useCallback,
  useDeferredValue,
  useEffect,
  useMemo,
  useRef,
  useState,
  type MouseEvent,
} from 'react';
import { FormattedMessage, useIntl } from 'react-intl';

type Props = {
  value?: CountryType;
  setValue: (value: CountryType) => void;
  className?: string;
  popoverClassName?: string;
  disabled?: boolean;
  /**
   * Optional extra countries to append to the dropdown options. Used to opt
   * specific flows (e.g. the join-team flow) into showing entries that are
   * intentionally absent from the global `countries` list — currently the
   * enterprise (`*_AZ1`) variants. Extras participate in the widget-region
   * filter, search, and selection callback just like normal countries.
   */
  extraCountries?: CountryType[];
  /**
   * When provided and a country is selected, shows a clear (✕) control that
   * unsets the selection. Opt-in so required country flows stay unchanged.
   */
  onClear?: () => void;
  /**
   * Limits the options to countries in this region (case-insensitive). A
   * region no country maps to leaves the full list.
   */
  regionFilter?: string;
};

const CountrySelect = ({
  setValue,
  value,
  className,
  popoverClassName,
  disabled,
  extraCountries,
  onClear,
  regionFilter,
}: Props) => {
  const intl = useIntl();
  const { localizedCountries, getCountryLocalizedName } = useLocalizedCountries();
  const widgetRegion = useAtomValue(widgetRegionAtom);
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');
  const searchValue = useDeferredValue(search.toLowerCase());
  const selectedCountry = value;
  const textRef = useRef<HTMLParagraphElement>(null);
  const [isTruncated, setIsTruncated] = useState(false);

  // Display the extras' literal `name` (e.g. "United Arab Emirates (Enterprise)")
  // rather than the i18n country name for their countryCode, which would drop
  // the "(Enterprise)" suffix.
  const isExtraCountry = !!extraCountries?.some((c) => c.name === selectedCountry?.name);
  const selectedCountryLocalizedName = selectedCountry
    ? isExtraCountry
      ? selectedCountry.name
      : getCountryLocalizedName(selectedCountry)
    : null;

  const checkTruncation = useCallback(() => {
    const el = textRef.current;
    if (el) {
      setIsTruncated(el.scrollWidth > el.clientWidth);
    }
  }, []);

  // Keep `isTruncated` in sync with the rendered label so the tooltip content
  // is mounted before Radix first opens the tooltip (fixes empty-on-first-hover).
  useEffect(() => {
    const el = textRef.current;
    if (!el) return;
    checkTruncation();
    if (typeof ResizeObserver === 'undefined') return;
    const observer = new ResizeObserver(() => checkTruncation());
    observer.observe(el);
    return () => observer.disconnect();
  }, [checkTruncation, selectedCountryLocalizedName]);

  // Localize extras using their literal `name` — Enterprise variants don't
  // need real i18n display names, and reusing `name` keeps search consistent.
  const localizedExtraCountries = useMemo<typeof localizedCountries>(
    () => (extraCountries ?? []).map((c) => ({ ...c, localizedName: c.name })),
    [extraCountries]
  );

  const allLocalizedCountries = useMemo(
    () => [...localizedCountries, ...localizedExtraCountries],
    [localizedCountries, localizedExtraCountries]
  );

  // filter countries based on the widget region
  const filteredCountries = useMemo(() => {
    const validWidgetRegion =
      widgetRegion &&
      Object.values(Region)
        .map((x) => x.toString())
        .includes(widgetRegion.toUpperCase());

    const inWidgetRegion = validWidgetRegion
      ? allLocalizedCountries.filter(
          (country) =>
            countriesToRegion[country.name]?.toUpperCase() === widgetRegion.toUpperCase()
        )
      : allLocalizedCountries;
    return filterCountriesByRegion(inWidgetRegion, regionFilter);
  }, [widgetRegion, allLocalizedCountries, regionFilter]);

  // Search both English name and localized name for better UX
  const searchedCountries = filteredCountries.filter(
    (country) =>
      country.name.toLowerCase().includes(searchValue) ||
      country.localizedName.toLowerCase().includes(searchValue)
  );

  const displayCountries = [...searchedCountries];

  const onCountrySelected = (val: string) => {
    const countrySelected =
      sortedCountries.find((ctry) => ctry.name === val) ??
      extraCountries?.find((ctry) => ctry.name === val);
    if (countrySelected) {
      setValue(countrySelected);
    }
    setOpen(false);
    setSearch('');
  };

  const handleClear = useCallback(
    (e: MouseEvent) => {
      e.stopPropagation();
      e.preventDefault();
      onClear?.();
    },
    [onClear]
  );
  const showClearButton = !!selectedCountry && !!onClear && !disabled;

  return (
    <div className={cn('flex w-full flex-col', className)}>
      <div className="w-full">
        <Popover open={open} onOpenChange={setOpen} modal={true}>
          <Tooltip>
            <div className="relative w-full">
              <TooltipTrigger asChild>
                <PopoverTrigger asChild>
                  <ComboboxTrigger
                    open={open}
                    disabled={disabled}
                    isPlaceholder={!selectedCountry}
                    data-testid="country-select"
                    role="combobox"
                    onKeyDown={(e) => {
                      if (e.key === 'Tab' && open) {
                        setOpen(false);
                        return;
                      }
                      if (!open && (e.key === 'ArrowDown' || e.key === 'ArrowUp')) {
                        e.preventDefault();
                        setOpen(true);
                      }
                    }}
                  >
                    <span
                      className={cn(
                        'flex min-w-0 flex-1 items-center gap-4',
                        showClearButton && 'pe-8'
                      )}
                    >
                      {selectedCountry?.countryCode && (
                        <Image
                          src={`/country-flags/3x2/${selectedCountry?.countryCode?.toUpperCase()}.svg`}
                          width={24}
                          height={16}
                          alt={selectedCountryLocalizedName || selectedCountry?.name}
                          className="h-[16px] w-[24px] rounded-sm"
                        />
                      )}
                      <TypographyP2 ref={textRef} className="truncate">
                        {selectedCountryLocalizedName || (
                          <FormattedMessage
                            defaultMessage="Select a country"
                            id="mPmOdpyPPb"
                            description="Placeholder shown when no country is selected"
                          />
                        )}
                      </TypographyP2>
                    </span>
                  </ComboboxTrigger>
                </PopoverTrigger>
              </TooltipTrigger>
              {showClearButton && (
                <Button
                  type="button"
                  variant="ghost"
                  size="xxs"
                  iconOnly
                  data-testid="country-select-clear"
                  className="absolute end-8 top-1/2 z-10 -translate-y-1/2 text-text-secondary"
                  aria-label={intl.formatMessage({
                    defaultMessage: 'Clear selection',
                    id: '1r1IWMqQl8',
                    description: 'Accessible label for clearing the selected country',
                  })}
                  onClick={handleClear}
                >
                  <X />
                </Button>
              )}
            </div>
            {isTruncated && !open && selectedCountryLocalizedName && (
              <TooltipContent side="bottom">
                {selectedCountryLocalizedName}
              </TooltipContent>
            )}
          </Tooltip>
          <PopoverContent
            matchTriggerWidth
            className={cn(
              'max-h-[--radix-popover-content-available-height] overflow-hidden p-0',
              popoverClassName
            )}
            sideOffset={4} // Adjust offset as needed
            align="center" // Aligns the content with the start of the trigger
            side="bottom"
            onKeyDownCapture={(e) => {
              if (e.key === 'Tab') {
                setOpen(false);
              }
            }}
          >
            <Command shouldFilter={false}>
              <CommandInput
                data-testid="county-select-input"
                placeholder={intl.formatMessage({
                  defaultMessage: 'Search for a country',
                  id: 'r/S+5KgwaO',
                  description: 'Placeholder for the country search input',
                })}
                onValueChange={(e) => setSearch(e)}
              />
              <CommandEmpty>
                <FormattedMessage
                  defaultMessage="No country found."
                  id="3B+fXPyRXD"
                  description="Empty state when no country matches the search"
                />
              </CommandEmpty>
              <ScrollArea className="max-h-[220px] overflow-scroll sm:overflow-auto">
                <CommandGroup>
                  {displayCountries.map((country) => (
                    <CommandItem
                      data-testid="country-select-item"
                      className="flex gap-3"
                      key={country.name}
                      value={country.name}
                      onSelect={() => {
                        onCountrySelected(country.name);
                      }}
                    >
                      {country.countryCode && (
                        <Image
                          src={`/country-flags/3x2/${country.countryCode.toUpperCase()}.svg`}
                          width={24}
                          height={16}
                          alt={country.localizedName}
                          className="h-[16px] w-[24px] rounded-sm"
                        />
                      )}
                      {country.localizedName}
                    </CommandItem>
                  ))}
                </CommandGroup>
              </ScrollArea>
            </Command>
          </PopoverContent>
        </Popover>
      </div>
    </div>
  );
};

export default CountrySelect;
